import React, { useState, useEffect, useMemo } from 'react';
import { Clock, AlertCircle, RefreshCw, Pause } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';

interface ResolutionTimeCountdownProps {
  resolutionTimeHours: number;
  resolutionTimeSetAt: string;
  resolutionTimePausedAt?: string | null;
  compact?: boolean;
  ticketId?: number;
  onRenew?: (newHours: number) => Promise<void>;
}

const ResolutionTimeCountdown: React.FC<ResolutionTimeCountdownProps> = ({
  resolutionTimeHours,
  resolutionTimeSetAt,
  resolutionTimePausedAt = null,
  compact = false,
  ticketId,
  onRenew
}) => {
  const { toast } = useToast();
  const [remainingTime, setRemainingTime] = useState<number | null>(null);
  const [hasNotified, setHasNotified] = useState(false);
  const [showRenewForm, setShowRenewForm] = useState(false);
  const [newResolutionHours, setNewResolutionHours] = useState<number | ''>('');
  const [isRenewing, setIsRenewing] = useState(false);

  // Verificar se o contador está pausado
  const isPaused = !!resolutionTimePausedAt;

  // Calcular tempo restante
  const calculateRemainingTime = useMemo(() => {
    if (!resolutionTimeSetAt) return null;

    // Se está pausado, calcular o tempo que estava quando foi pausado
    if (isPaused && resolutionTimePausedAt) {
      const setAt = new Date(resolutionTimeSetAt).getTime();
      const pausedAt = new Date(resolutionTimePausedAt).getTime();
      const elapsedMs = pausedAt - setAt;
      const elapsedHours = elapsedMs / (1000 * 60 * 60);
      const remaining = resolutionTimeHours - elapsedHours;
      return Math.max(0, Math.round(remaining * 100) / 100);
    }

    // Contador ativo - calcular normalmente
    const setAt = new Date(resolutionTimeSetAt).getTime();
    const now = Date.now();
    const elapsedMs = now - setAt;
    const elapsedHours = elapsedMs / (1000 * 60 * 60);
    const remaining = resolutionTimeHours - elapsedHours;

    return Math.max(0, Math.round(remaining * 100) / 100);
  }, [resolutionTimeHours, resolutionTimeSetAt, isPaused, resolutionTimePausedAt]);

  useEffect(() => {
    // Atualizar imediatamente
    setRemainingTime(calculateRemainingTime);

    // Se está pausado, não atualizar mais
    if (isPaused) {
      return;
    }

    // Atualizar a cada minuto apenas se não estiver pausado
    const interval = setInterval(() => {
      const setAt = new Date(resolutionTimeSetAt).getTime();
      const now = Date.now();
      const elapsedMs = now - setAt;
      const elapsedHours = elapsedMs / (1000 * 60 * 60);
      const remaining = resolutionTimeHours - elapsedHours;
      const newRemaining = Math.max(0, Math.round(remaining * 100) / 100);
      
      setRemainingTime(newRemaining);

      // Notificar quando chegar a 0 (apenas uma vez)
      if (newRemaining <= 0 && !hasNotified) {
        setHasNotified(true);
        toast({
          title: 'Tempo de resolução esgotado',
          description: 'O tempo previsto para resolução deste ticket expirou.',
          variant: 'destructive'
        });
      }
    }, 60000); // Atualizar a cada minuto

    return () => clearInterval(interval);
  }, [resolutionTimeHours, resolutionTimeSetAt, calculateRemainingTime, hasNotified, toast, isPaused]);

  if (remainingTime === null) {
    return null;
  }

  // Calcular porcentagem restante
  const percentageRemaining = (remainingTime / resolutionTimeHours) * 100;

  // Determinar cor baseado na porcentagem
  let badgeVariant: 'default' | 'secondary' | 'destructive' | 'outline' = 'default';
  let badgeClassName = '';
  let icon = <Clock className="h-3 w-3" />;

  if (remainingTime <= 0) {
    badgeVariant = 'destructive';
    badgeClassName = 'bg-red-500 text-white';
    icon = <AlertCircle className="h-3 w-3" />;
  } else if (percentageRemaining < 10) {
    badgeVariant = 'destructive';
    badgeClassName = 'bg-red-400 text-white';
  } else if (percentageRemaining < 50) {
    badgeVariant = 'default';
    badgeClassName = 'bg-yellow-500 text-white';
  } else {
    badgeVariant = 'default';
    badgeClassName = 'bg-green-500 text-white';
  }

  // Formatar tempo restante
  const formatTime = (hours: number): string => {
    if (hours <= 0) {
      return 'Tempo esgotado';
    }

    const wholeHours = Math.floor(hours);
    const minutes = Math.floor((hours - wholeHours) * 60);

    if (wholeHours === 0) {
      return `${minutes} min`;
    } else if (minutes === 0) {
      return `${wholeHours} ${wholeHours === 1 ? 'hora' : 'horas'}`;
    } else {
      return `${wholeHours}h ${minutes}min`;
    }
  };

  const handleRenew = async () => {
    if (!onRenew || !newResolutionHours || Number(newResolutionHours) < 1) {
      toast({
        title: 'Valor inválido',
        description: 'A previsão deve ser de pelo menos 1 hora',
        variant: 'destructive'
      });
      return;
    }

    try {
      setIsRenewing(true);
      await onRenew(Number(newResolutionHours));
      setShowRenewForm(false);
      setNewResolutionHours('');
      setHasNotified(false); // Resetar notificação para permitir nova notificação se necessário
      toast({
        title: 'Prazo renovado',
        description: `Novo prazo de ${newResolutionHours} ${Number(newResolutionHours) === 1 ? 'hora' : 'horas'} definido com sucesso.`
      });
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error?.response?.data?.error || 'Erro ao renovar prazo',
        variant: 'destructive'
      });
    } finally {
      setIsRenewing(false);
    }
  };

  const timeExpired = remainingTime <= 0;
  const showRenewOption = timeExpired && onRenew && !showRenewForm;

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <Badge variant={badgeVariant} className={`text-xs ${badgeClassName}`}>
          {isPaused ? <Pause className="h-3 w-3" /> : icon}
          <span className="ml-1">
            {isPaused ? 'Pausado' : formatTime(remainingTime)}
          </span>
        </Badge>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
        {isPaused ? <Pause className="h-4 w-4 text-yellow-500" /> : icon}
        <div className="flex-1">
          <div className="text-sm font-medium">
            Tempo restante: {isPaused ? 'Pausado' : formatTime(remainingTime)}
            {isPaused && (
              <span className="ml-2 text-xs text-muted-foreground">
                (Contador pausado após resposta no histórico)
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Previsão: {resolutionTimeHours} {resolutionTimeHours === 1 ? 'hora' : 'horas'}
          </div>
        </div>
        <Badge variant={badgeVariant} className={badgeClassName}>
          {isPaused ? '—' : `${Math.round(percentageRemaining)}%`}
        </Badge>
      </div>

      {/* Mostrar opção de renovar quando tempo esgotar */}
      {showRenewOption && (
        <div className="p-3 border rounded-md bg-yellow-50 border-yellow-200">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <span className="text-sm font-semibold text-yellow-900">
              Tempo esgotado
            </span>
          </div>
          <p className="text-xs text-yellow-700 mb-3">
            O prazo de resolução expirou. Deseja definir um novo prazo?
          </p>
          <Button
            onClick={() => setShowRenewForm(true)}
            size="sm"
            variant="outline"
            className="w-full"
          >
            <RefreshCw className="h-3 w-3 mr-2" />
            Renovar Prazo
          </Button>
        </div>
      )}

      {/* Formulário de renovação */}
      {showRenewForm && (
        <div className="p-3 border rounded-md bg-blue-50 border-blue-200">
          <div className="flex items-center gap-2 mb-2">
            <RefreshCw className="h-4 w-4 text-blue-600" />
            <span className="text-sm font-semibold text-blue-900">
              Renovar Prazo de Resolução
            </span>
          </div>
          <p className="text-xs text-blue-700 mb-3">
            Defina quantas horas são necessárias para resolver este ticket
          </p>
          <div className="flex gap-2">
            <Input
              type="number"
              min="1"
              placeholder="Ex: 24"
              value={newResolutionHours}
              onChange={(e) => setNewResolutionHours(e.target.value ? Number(e.target.value) : '')}
              className="flex-1"
            />
            <span className="self-center text-sm text-muted-foreground">horas</span>
            <Button
              onClick={handleRenew}
              disabled={isRenewing || !newResolutionHours || Number(newResolutionHours) < 1}
              size="sm"
            >
              {isRenewing ? 'Renovando...' : 'Renovar'}
            </Button>
            <Button
              onClick={() => {
                setShowRenewForm(false);
                setNewResolutionHours('');
              }}
              variant="outline"
              size="sm"
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResolutionTimeCountdown;

